import { useEffect } from "react";
import { useLocation } from "react-router-dom";

export const SITE_NAME = "Coastal Bizz Connect";

/**
 * Public site origin. Driven by VITE_SITE_URL so the same build can serve
 * staging and production; falls back to the production origin when unset.
 * Trailing slashes are stripped so canonical URLs never double up.
 */
const rawSiteUrl = import.meta.env.VITE_SITE_URL as string | undefined;
export const SITE_URL = (rawSiteUrl && rawSiteUrl.trim() ? rawSiteUrl : "https://www.coastalbizzconnect.com").replace(/\/+$/, "");
export const DEFAULT_OG_IMAGE =
  "https://readdy.ai/api/search-image?query=Scenic%20stylised%20illustration%20of%20the%20coastal%20Karnataka%20coastline%20with%20fishing%20boats%2C%20palm%20trees%20and%20a%20warm%20golden%20sunset%20over%20the%20Arabian%20Sea%2C%20artistic%20editorial%20travel%20art&width=1200&height=630&seq=cbc-seo-default-01&orientation=landscape";

export interface SeoProps {
  title: string;
  description: string;
  keywords?: string;
  path?: string;
  image?: string;
  type?: "website" | "article";
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

const STRUCTURED_DATA_ID = "cbc-structured-data";

function upsertMeta(attribute: "name" | "property", key: string, content: string) {
  let element = document.head.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
}

function upsertLink(rel: string, href: string) {
  let element = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", rel);
    document.head.appendChild(element);
  }
  element.setAttribute("href", href);
}

/**
 * Manages per-page document metadata for the SPA: title, description, keywords,
 * canonical URL, Open Graph / Twitter tags and optional JSON-LD structured data.
 * React 19 native metadata is not relied upon so this works consistently in every route.
 */
export default function Seo({
  title,
  description,
  keywords,
  path,
  image = DEFAULT_OG_IMAGE,
  type = "website",
  jsonLd,
}: SeoProps) {
  const location = useLocation();
  const canonical = `${SITE_URL}${path ?? location.pathname}`;
  const fullTitle = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;
  const jsonLdString = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    document.title = fullTitle;
    upsertMeta("name", "description", description);
    if (keywords) {
      upsertMeta("name", "keywords", keywords);
    }
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", canonical);
    upsertMeta("property", "og:image", image);

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", fullTitle);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", image);
  }, [fullTitle, description, keywords, canonical, image, type]);

  useEffect(() => {
    const existing = document.getElementById(STRUCTURED_DATA_ID);
    if (existing) {
      existing.remove();
    }
    if (!jsonLdString) {
      return undefined;
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = STRUCTURED_DATA_ID;
    script.text = jsonLdString;
    document.head.appendChild(script);
    return () => {
      const current = document.getElementById(STRUCTURED_DATA_ID);
      if (current) {
        current.remove();
      }
    };
  }, [jsonLdString]);

  return null;
}